// src/pages/Dashboard/Admin/Articles/utils/articlesDashboardUtils.js

export const ARTICLE_FALLBACK_IMAGE = "/img/no-image.jpg";

export function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

export function toBool(value, defaultValue = false) {
  if (typeof value === "boolean") return value;
  if (value === 1 || value === "1") return true;
  if (value === 0 || value === "0") return false;
  return defaultValue;
}

export function toAmount(value) {
  const number = Number.parseFloat(value || 0);
  return Number.isFinite(number) ? number : 0;
}

export function formatEuro(value) {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(toAmount(value));
}

export function normalizeText(value = "") {
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export function isLicenceCotisationArticle(article) {
  const text = normalizeText(
    `${article?.nom || ""} ${article?.categorie || ""} ${
      article?.description || ""
    }`
  );

  return (
    text.includes("licence") ||
    text.includes("cotisation") ||
    text.includes("assurance") ||
    text.includes("reduction") ||
    text.includes("pass sport") ||
    text.includes("pass'sport")
  );
}

export function sortArticlesByOrder(rows) {
  return safeArray(rows)
    .slice()
    .sort((a, b) => {
      const orderA = Number(a?.ordre_article || 0);
      const orderB = Number(b?.ordre_article || 0);

      if (orderA !== orderB) return orderA - orderB;

      return String(a?.nom || "").localeCompare(String(b?.nom || ""), "fr");
    });
}

export function normalizeOrder(rows) {
  return sortArticlesByOrder(rows).map((article, index) => ({
    ...article,
    ordre_article: index + 1,
  }));
}

export function getCategoryOptions(articles) {
  return Array.from(
    new Set(
      safeArray(articles)
        .map((article) => String(article?.categorie || "").trim())
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b, "fr"));
}

export function filterArticles(articles, filters) {
  const query = normalizeText(filters?.query || "");
  const category = String(filters?.category || "");
  const stockable = String(filters?.stockable || "");
  const active = String(filters?.active || "");

  return sortArticlesByOrder(articles).filter((article) => {
    const searchable = normalizeText(
      `${article?.nom || ""} ${article?.description || ""} ${
        article?.categorie || ""
      } ${article?.taille || ""}`
    );

    const matchQuery = !query || searchable.includes(query);
    const matchCategory = !category || article?.categorie === category;

    const matchStockable =
      !stockable ||
      (stockable === "yes"
        ? !!article?.is_stockable
        : !article?.is_stockable);

    const matchActive =
      !active ||
      (active === "yes" ? !!article?.is_active : !article?.is_active);

    return matchQuery && matchCategory && matchStockable && matchActive;
  });
}

export function calculateArticlesStats(articles) {
  const rows = safeArray(articles);

  let active = 0;
  let hidden = 0;
  let stockable = 0;
  let lowStock = 0;
  let outOfStock = 0;
  let noImage = 0;
  let totalStockValue = 0;
  let boutique = 0;
  let licenceCotisation = 0;

  const categories = new Set();

  for (const article of rows) {
    const price = toAmount(article?.prix);
    const quantity = Number(article?.quantite_stock || 0);

    if (article?.is_active) active += 1;
    else hidden += 1;

    if (article?.is_stockable) {
      stockable += 1;
      totalStockValue += price * quantity;

      if (quantity <= 0) outOfStock += 1;
      else if (quantity <= 3) lowStock += 1;
    }

    if (!article?.photo_url) noImage += 1;

    if (article?.categorie) categories.add(article.categorie);

    if (isLicenceCotisationArticle(article)) licenceCotisation += 1;
    else boutique += 1;
  }

  return {
    total: rows.length,
    active,
    hidden,
    stockable,
    lowStock,
    outOfStock,
    noImage,
    totalStockValue,
    categoriesCount: categories.size,
    boutique,
    licenceCotisation,
  };
}

export function getDefaultArticleForm(order = 1) {
  return {
    nom: "",
    description: "",
    prix: "",
    taille: "",
    quantite_stock: "",
    categorie: "",
    newCategorie: "",
    photo_url: "",
    is_active: true,
    is_stockable: true,
    ordre_article: order,
  };
}

export function mapArticleToForm(article, order = 1) {
  if (!article) return getDefaultArticleForm(order);

  return {
    nom: article?.nom || "",
    description: article?.description || "",
    prix: article?.prix ?? "",
    taille: article?.taille || "",
    quantite_stock: article?.quantite_stock ?? "",
    categorie: article?.categorie || "",
    newCategorie: "",
    photo_url: article?.photo_url || "",
    is_active: toBool(article?.is_active, true),
    is_stockable: toBool(article?.is_stockable, false),
    ordre_article: article?.ordre_article ?? order,
  };
}

export function buildArticlePayload(formData) {
  const finalCategory =
    formData.categorie === "_new"
      ? String(formData.newCategorie || "").trim()
      : String(formData.categorie || "").trim();

  return {
    nom: String(formData.nom || "").trim(),
    description: String(formData.description || "").trim(),
    prix: formData.prix === "" ? null : Number(formData.prix),
    taille: String(formData.taille || "").trim(),
    quantite_stock:
      formData.is_stockable && formData.quantite_stock !== ""
        ? Number(formData.quantite_stock)
        : 0,
    categorie: finalCategory,
    photo_url: String(formData.photo_url || "").trim(),
    is_active: !!formData.is_active,
    is_stockable: !!formData.is_stockable,
    ordre_article:
      formData.ordre_article === "" ? 0 : Number(formData.ordre_article),
  };
}